import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import inductusLogo from "@/assets/inductus-logo.webp";
const navigation = [{
  name: "Solutions",
  href: "/solutions"
}, {
  name: "Who We Work With",
  href: "/who-we-work-with"
}, {
  name: "How We Work",
  href: "/how-we-work"
}, {
  name: "Insights",
  href: "/insights"
}, {
  name: "About",
  href: "/about"
}];
const mobileMenuVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut" as const
    }
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.3,
      ease: "easeInOut" as const
    }
  }
};
const linkVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: "easeOut" as const
    }
  })
};
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-sm" : "bg-transparent"}`}>
      <nav className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              alt="Inductus International" 
              width={123} 
              height={35} 
              className="h-10 md:h-12 w-auto transition-transform duration-200 hover:scale-[1.02] rounded-lg shadow opacity-85 border border-none" 
              fetchPriority="high" 
              decoding="async"
              loading="eager"
              src={inductusLogo}
              sizes="(max-width: 768px) 100px, 123px"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map(item => <Link key={item.name} to={item.href} className={`font-sans text-sm font-medium tracking-wide transition-colors link-underline ${location.pathname === item.href ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {item.name}
              </Link>)}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }}>
              <Button asChild className="btn-primary rounded-none">
                <Link to="/request-assessment">Request Assessment</Link>
              </Button>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button className="lg:hidden p-2 text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" whileTap={{
          scale: 0.95
        }}>
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? <motion.div key="close" initial={{
              rotate: -90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: 90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }}>
                  <X size={24} />
                </motion.div> : <motion.div key="menu" initial={{
              rotate: 90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: -90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }}>
                  <Menu size={24} />
                </motion.div>}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && <motion.div className="lg:hidden absolute top-20 left-0 right-0 bg-background border-b border-border shadow-lg overflow-hidden" variants={mobileMenuVariants} initial="hidden" animate="visible" exit="hidden">
              <div className="px-6 py-6 space-y-4">
                {navigation.map((item, i) => <motion.div key={item.name} custom={i} variants={linkVariants} initial="hidden" animate="visible">
                    <Link to={item.href} className={`block font-sans text-base font-medium transition-colors ${location.pathname === item.href ? "text-foreground" : "text-muted-foreground"}`}>
                      {item.name}
                    </Link>
                  </motion.div>)}
                <motion.div className="pt-4 border-t border-border" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.3
            }}>
                  <Button asChild className="w-full btn-primary rounded-none">
                    <Link to="/request-assessment">Request Assessment</Link>
                  </Button>
                </motion.div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
};